import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar, MapPin, Users, Wifi, Zap, Shield, Truck, Sparkles, Loader2, Flag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import RatingStars from "../components/RatingStars";
import Badges from "../components/Badges";
import MapView from "../components/MapView";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import useSEO from "../lib/useSEO";

const serviceIcons = {
  electricite: Zap, wifi: Wifi, parking_exposants: Truck,
  securite: Shield, gardiennage: Shield,
};

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);

  const applyPromo = async () => {
    const firstBooth = event?.booths?.[0];
    if (!promoInput.trim() || !firstBooth) return;
    try {
      const { data } = await api.post("/promo/validate", {
        code: promoInput, item_type: "booth", item_id: firstBooth.id,
      });
      setPromo(data);
      toast.success(`Code ${data.code} appliqué : −${data.percent}%`);
    } catch (e) {
      setPromo(null);
      toast.error(e.response?.data?.detail || "Code promo invalide");
    }
  };
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");

  useSEO({
    title: event?.title || "Événement",
    description: event
      ? `${event.title} — ${event.city || ""}. ${(event.description || "").slice(0, 160)}`
      : "Découvrez cet événement sur EventMarket Pro.",
    image: event?.cover_image || event?.images?.[0],
    type: "article",
  });

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${id}`);
      setEvent(data);
    } catch {
      toast.error("Événement introuvable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Track view (fire-and-forget, deduped server-side per 24h)
  useEffect(() => {
    if (!id) return;
    api.post(`/events/${id}/view`).catch(() => {});
  }, [id]);

  const reserve = async (booth) => {
    if (!user) { nav("/login"); return; }
    setBooking(true);
    try {
      const { data } = await api.post("/checkout/session", {
        item_type: "booth",
        item_id: booth.id,
        event_id: event.id,
        quantity: 1,
        origin_url: window.location.origin,
        promo_code: promo?.code || null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de paiement");
      setBooking(false);
    }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", {
        target_type: "event", target_id: event.id,
        rating: reviewRating, comment: reviewComment, criteria: {},
      });
      toast.success("Avis publié");
      setReviewComment("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const reportReview = async (reviewId) => {
    const reason = window.prompt("Motif du signalement de cet avis :");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post("/reports", { target_type: "review", target_id: reviewId, reason });
      toast.success("Signalement envoyé à la modération");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12"><Loader2 className="animate-spin h-6 w-6" /></div>;
  if (!event) return null;

  const img = event.images?.[0] || "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1600";
  const start = new Date(event.start_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const end = new Date(event.end_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="aspect-video rounded-md overflow-hidden mb-8 bg-cream-100">
        <img src={img} alt={event.title} className="w-full h-full object-cover" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Badge variant="outline" className="uppercase tracking-wider">{event.category}</Badge>
            {event.rating_avg > 0 && <RatingStars rating={event.rating_avg} count={event.rating_count} />}
          </div>
          <h1 data-testid="event-title" className="font-heading font-light tracking-editorial text-4xl lg:text-5xl text-primary leading-tight mb-6">
            {event.title}
          </h1>
          {event.badges?.length > 0 && (
            <div className="mb-8">
              <Badges badges={event.badges} variant="detail" />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 text-sm">
            <div className="flex items-start gap-2"><Calendar className="h-4 w-4 mt-0.5 text-accent" /><div><p className="font-semibold">Dates</p><p className="text-muted-foreground">Du {start}<br />au {end}</p></div></div>
            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-accent" /><div><p className="font-semibold">Lieu</p><p className="text-muted-foreground">{event.address}<br />{event.city}</p></div></div>
            <div className="flex items-start gap-2"><Users className="h-4 w-4 mt-0.5 text-accent" /><div><p className="font-semibold">Organisateur</p>
              {event.organizer?.user_id ? (
                <Link to={`/users/${event.organizer.user_id}`} data-testid="organizer-link" className="text-muted-foreground hover:text-accent transition-colors">
                  {event.organizer.name}
                </Link>
              ) : (
                <p className="text-muted-foreground">{event.organizer?.name}</p>
              )}
            </div></div>
          </div>

          <div className="prose max-w-none mb-8">
            <p className="text-muted-foreground leading-relaxed">{event.description}</p>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-medium text-xl text-ink mb-4 tracking-tight">Services disponibles</h2>
            <div className="flex flex-wrap gap-2">
              {event.services?.map((s) => {
                const Icon = serviceIcons[s] || Sparkles;
                return (
                  <span key={s} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md bg-cream-100 text-ink/80">
                    <Icon className="h-3 w-3" /> {s.replace(/_/g, " ")}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Mini-map */}
          {typeof event.lat === "number" && typeof event.lng === "number" && (
            <div className="mb-10" data-testid="event-mini-map">
              <h2 className="font-heading font-medium text-xl text-ink mb-4 tracking-tight">Emplacement</h2>
              <div className="h-72">
                <MapView
                  items={[{
                    id: event.id, title: event.title, city: event.city,
                    lat: event.lat, lng: event.lng, start_date: event.start_date,
                  }]}
                  type="event"
                  height="100%"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {event.address} · {event.city}
              </p>
            </div>
          )}

          {/* Reviews */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-medium text-xl text-primary tracking-tight">Avis ({event.reviews?.length || 0})</h2>
              {user && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button data-testid="review-cta" variant="outline" size="sm">Laisser un avis</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Noter cet événement</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium mb-2">Note</p>
                        <div className="flex gap-1">
                          {[1,2,3,4,5].map(i => (
                            <button key={i} onClick={() => setReviewRating(i)} type="button">
                              <span className={`text-2xl ${i <= reviewRating ? "text-accent" : "text-ink/30"}`}>★</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <Textarea
                        placeholder="Partagez votre expérience..."
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        data-testid="review-textarea"
                      />
                      <Button onClick={submitReview} data-testid="review-submit" className="w-full bg-ink hover:bg-ink/85 rounded-none">Publier</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            {event.reviews?.length ? event.reviews.map((r) => (
              <div key={r.id} className="bg-white border border-ink/10 rounded-md p-4 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{r.user_name}</p>
                    {r.verified && <Badge className="bg-trust/10 text-trust border border-trust/20 text-xs">Vérifié</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <RatingStars rating={r.rating} showNumber={false} size={14} />
                    {user && user.user_id !== r.user_id && (
                      <button
                        onClick={() => reportReview(r.id)}
                        title="Signaler cet avis"
                        data-testid={`review-report-${r.id}`}
                        className="text-ink/30 hover:text-destructive p-1"
                      >
                        <Flag className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{r.comment}</p>
              </div>
            )) : <p className="text-sm text-muted-foreground">Pas encore d'avis.</p>}
          </div>
        </div>

        {/* Sidebar: Booths */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="bg-white border border-ink/10 rounded-md p-6">
            <p className="text-label text-muted-foreground mb-3">Réserver un stand</p>
            <h3 className="font-heading font-medium text-lg text-ink mb-5 tracking-tight">{event.booths?.length || 0} options disponibles</h3>
            {event.booths?.length > 0 && (
              <div className="mb-4">
                <div className="flex gap-2">
                  <input
                    data-testid="promo-code-field"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                    placeholder="Code promo"
                    className="flex-1 min-w-0 border border-ink/15 rounded-none px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:border-ink"
                  />
                  <Button data-testid="promo-apply-btn" variant="outline" size="sm" className="rounded-none h-auto" onClick={applyPromo}>
                    Appliquer
                  </Button>
                </div>
                {promo && (
                  <p data-testid="promo-applied-label" className="text-xs text-trust mt-2">
                    ✓ Code <span className="font-mono font-bold">{promo.code}</span> appliqué : −{promo.percent}% sur votre réservation
                  </p>
                )}
              </div>
            )}
            <div className="space-y-3">
              {event.booths?.map((b) => (
                <div key={b.id} className="border border-ink/10 rounded-md p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-ink">{b.name}</p>
                      <p className="text-xs text-muted-foreground">{b.size} · {b.available} disponibles</p>
                    </div>
                    {promo ? (
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground line-through">{b.price}€</p>
                        <p className="font-heading font-black text-xl text-accent" data-testid={`booth-discounted-price-${b.id}`}>
                          {(b.price * (1 - promo.percent / 100)).toFixed(2)}€
                        </p>
                      </div>
                    ) : (
                      <p className="font-heading font-black text-xl text-ink">{b.price}€</p>
                    )}
                  </div>
                  <Button
                    data-testid={`booth-reserve-${b.id}`}
                    onClick={() => reserve(b)}
                    disabled={booking || b.available === 0}
                    className="w-full bg-ink hover:bg-ink/85 text-cream rounded-none"
                    size="sm"
                  >
                    {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : b.available === 0 ? "Complet" : "Réserver"}
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-ink/10">
              Commission plateforme 20% incluse. Paiement sécurisé via Stripe.
            </p>
          </div>

          {event.venue && (
            <Link to={`/venues/${event.venue.id}`} className="block mt-4 bg-white border border-ink/10 rounded-md p-4 hover:border-ink/20">
              <p className="text-label text-muted-foreground mb-2">Lieu de l'événement</p>
              <p className="font-heading font-medium text-ink">{event.venue.name}</p>
              <p className="text-xs text-muted-foreground">{event.venue.city}</p>
            </Link>
          )}
        </aside>
      </div>
    </div>
  );
}
